import {
  Component,
  AfterViewInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnInit,
  OnChanges,
  SimpleChanges,
  NgZone,
} from '@angular/core';
import { Router } from '@angular/router';
import OpenSeaDragon from 'openseadragon';
import { MetaService } from '@shared/services/meta.service';
import { Image } from '@shared/models/image';
import { Icon } from '@app/@shared/components/generic-toolbar/icons';
import { ToolbarTypes } from '@shared/components/generic-toolbar/toolbar-type';
import { CustomOption, OptionGroup, Option, SocialOption, CustomLargeOption } from '@app/@shared/components/generic-toolbar/option-model';
import { StatelessBehavior, ToggleBehavior } from '@app/@shared/components/generic-toolbar/state-behavior-model';
import { Metadata } from '@shared/models/metadata';
import { TelemetryEvent } from '@shared/models/telemetry-event';
import { TelemetryEventName } from '@shared/enums';
import { moduleNameFromUrl } from '@shared/utils/common';
import { ShareItemModalComponent } from '../share-item-modal/share-item-modal.component';
import { DialogService } from '@app/@shared/services/dialog/dialog.service';
import { RequestImageModalComponent } from '@shared/components/request-image-modal/request-image-modal.component';
import { AbstractImageViewerComponent } from '../abstract/abstract-image-viewer/abstract-image-viewer/abstract-image-viewer.component';
import { Measurement } from '@app/@shared/models/kimera/measurement';
import { AnnotationService } from '@app/@shared/services/annotation/annotation.service';
import { CalibrationInstructionsModalComponent } from './calibration-instructions-modal/calibration-instructions-modal.component';
import { Calibration } from '@app/@shared/models/calibration/calibration.model';
import { ImageCalibrationModalComponent } from './image-calibration-modal/image-calibration-modal.component';
import { ImageMeasurementDialogComponent } from './image-measurement-dialog/image-measurement-dialog.component';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import {isTablet} from "@app/group/helpers/is-tablet";
import {ImageLoadingModalComponent} from "@shared/components/image-loading-modal/image-loading-modal.component";
import {ThumbnailsService} from "@shared/services/thumbnails/thumbnails.service";
import {
  CalibrationModalWarningComponent
} from "@shared/components/calibration-modal-warning/calibration-modal-warning.component";
import {FeatureFlagService, ffKeys} from '@app/@shared/services/featureflag.service';
import {combineLatest} from "rxjs";
import { finalize } from 'rxjs/operators';
import { OsdKimeraService } from '@app/osd-annotation/services/osd-kimera.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { untilDestroyed } from '@core';
import { IMAGE_SOURCE_TYPE } from "@shared/consts/platform-constants";

// Exported for unit tests.
//  Should probably be GenericToolbarEvent exported from the generic toolbar.
export interface ImageViewerToolbarEvent {
  key: string;
  value: any;
}

interface ToolsConfig {
  zoomIn: boolean;
  zoomOut: boolean;
  rotateRight: boolean;
  requestImage: boolean;
  toggleFullscreen: boolean;
  fitToPage: boolean;
  toggleNavigator: boolean;
  prevPage: boolean;
  nextPage: boolean;
  closeViewer: boolean;
  downloadImage: boolean;
  shareImage: boolean;
  addToFavorites: boolean;
  toggleLengthSelection: boolean;
  toggleImageResolution: boolean;
  annotateImage: boolean;
}

export type ImageViewerToolsConfiguration = Partial<ToolsConfig>;

const defaultZoomOptionsForMouse = {
  scrollToZoom: true,
  clickToZoom: false,
  dblClickToZoom: true,
  pinchToZoom: true,
};

declare function initializeOpenSeaDragonSvgRelay(openseadragon: any): any;

@UntilDestroy()
@Component({
    selector: 'app-image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss'],
    standalone: false
})
export class ImageViewerComponent
  extends AbstractImageViewerComponent
  implements AfterViewInit, OnDestroy, OnInit, OnChanges
{

  @Input() thumbnailImages: Array<Image>;
  @Input() numberOfImages: number;
  @Input() imageNumber: number;
  @Input() subscriptionId: string;
  @Input() channelId: string;
  @Input() downloadEnabled = false;
  @Input() enableUnwatermarkedImageRequest = false;
  @Input() addToFavoriteEnabled = false;

  @Input() set selectedImage(image: Image) {
    this._selectedImage = image;
  }

  get selectedImage() {
    return this._selectedImage;
  }

  @Input() toolbarConfig : ImageViewerToolsConfiguration | boolean = true;
  @Input() toolbarType = ToolbarTypes.ANALYSIS_IMAGE_VIEWER;

  @Output() toggleImageResolution: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeImageViewer: EventEmitter<any> = new EventEmitter<any>();
  @Output() thumbnailClicked = new EventEmitter<string>();
  @Output() nextImage = new EventEmitter<void>();
  @Output() previousImage = new EventEmitter<void>();
  @Output() downloadImage = new EventEmitter<Image>();
  @Output() addToFavorites = new EventEmitter<Image>();

  public toolbarOptions: Array<OptionGroup> = null;
  public toolbarTitle = '';
  public metadata: Metadata;
  public isFullScreen = false;
  public fullscreenToggle: CustomOption = null;
  public lengthSelectionToggleOption: CustomOption;
  public toggleImageResolutionOption: CustomLargeOption;

  private isTablet: boolean;
  private readonly openSeadragonInitialSettings = {
    id: 'openseadragon', // HTML ID in the template of the element to embed the viewer
    prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
    showNavigator: true,
    navigatorAutoFade: false,
    showThumbnails: true,
    toolbar: 'toolbar', // HTML ID of the element shown as the toolbar in fullscreen mode
    gestureSettingsMouse: defaultZoomOptionsForMouse,
    preload: false,
    maxZoomPixelRatio: 50,
    visible: true,
    tileSources: {
      type: 'image',
      url: 'assets/NotFound/Placeholder.svg',
    },
  };
  private showNavigator = true;
  private calibration: Calibration;
  private readonly CALIBRATION_INSTRUCTIONS_MODAL_OPTIONS: NgbModalOptions = {
    windowClass: 'calibration-instructions-dialog',
    size: 'lg',
    container: '.openseadragon-viewer',
  };
  private enableToggleImageResolution: boolean;
  private enableImageAnnotation: boolean;

  // this is more like a workaround. When toggle for HD, we need to go back to SD. But
  // ImageviewerComponent does not get images, it receives them from parent component.
  // So we need to store the current image in order to go back to it.
  private _selectedImageSDVersion: Image;

  constructor(
    _ngZone: NgZone,
    metaService: MetaService,
    private readonly router: Router,
    dialogService: DialogService,
    annotationService: AnnotationService,
    private readonly thumbnailsService: ThumbnailsService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly osdKimeraService: OsdKimeraService,
  ) {
    super(_ngZone, metaService, dialogService, annotationService);
  }

  /**
   * onKeyUp allows the left/right arrow keys to browse previous/next images.
   *  Note that this is overriding the default pan behaviour of OpenSeadragon.
   * @param event The keyup event.
   */
  @HostListener('window:keyup', ['$event'])
  public onKeyUp(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') {
      return this.onClickNextImage();
    } else if (event.key === 'ArrowLeft') {
      return this.onClickPreviousImage();
    }
  }

  ngOnInit() {
    combineLatest([
      this.featureFlagService.getFlag(ffKeys.enableImageResolutionToggle),
      this.featureFlagService.getFlag(ffKeys.enableImageAnnotationTool),
      isTablet()
    ]).subscribe(([ enableImageResolutionToggle, enableImageAnnotation, isTablet ]) => {
      this.enableToggleImageResolution = enableImageResolutionToggle;
      this.enableImageAnnotation = enableImageAnnotation;
      this.isTablet = isTablet;
      this.toolbarOptions = this.createToolbarOptions();
    });
    this.fullscreenToggle = this.getFullScreenToggle();
    this.hasCalibration = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedImage) {
      this.lengthSelectionToggleOption?.resetState();
      this.toggleImageResolutionOption?.resetState();
      this.measuringMode = null;

      const previousImage = changes.selectedImage.previousValue;
      const currentImage = changes.selectedImage.currentValue;

      // Check if the previous image is different from the actual one
      if (
        (!previousImage && currentImage) ||
        (previousImage && currentImage && previousImage.objectId !== currentImage.objectId)
      ) {
        this.postTelemetryEventForOpeningImage();
        this.hasCalibration = false;
        if(this.isToolEnabled('toggleLengthSelection')) {
          this.annotationService.getCalibration(currentImage.id).subscribe((calibration) => {
            this.hasCalibration = !!calibration;
            this.calibration = calibration;
          });
        }
      }

      this.metadata = this.createMetadataFromImage(this._selectedImage);
      this.toolbarTitle = this.metadata.getValue('Filename') || this.metadata.getValue('File Name');
      this.viewer?.viewport?.setRotation(0);
      this.displaySelectedImageInOpenSeadragon();
    }
  }

  // ngOnDestroy is necessary for the use of untilDestroyed.
  // The return statement fakes out SonarCloud into thinking the method isn't empty.
  public ngOnDestroy(): void {
    return;
  }

  public ngAfterViewInit() {
    this.startOpenSeadragon();
    this.viewer.addHandler('full-screen', (_data: any) => {
      this.fullscreenToggle?.stateBehavior.onChange();
      this.isFullScreen = !this.isFullScreen;
    });
    // Remove empty div that OpenSeadragon adds to the 'toolbar' element
    document.querySelector('#toolbar').lastChild.remove();
    this.displaySelectedImageInOpenSeadragon();
  }

  public onClickZoomIn(): void {
    const currentZoom = this.viewer.viewport.getZoom();
    this.viewer.viewport.zoomTo(currentZoom + 0.1);
  }

  public onClickZoomOut(): void {
    const currentZoom = this.viewer.viewport.getZoom();
    const newZoom = currentZoom - 0.1;
    this.viewer.viewport.zoomTo(newZoom > 0.1 ? newZoom : 0.1);
  }

  public onClickFitPage(): void {
    // true causes to fill instantly instead of quick zoom in/out
    this.viewer.viewport.fitHorizontally(true);
  }

  public onClickRotateRight(): void {
    const currentAngle = this.viewer.viewport.getRotation();
    this.viewer.viewport.setRotation(currentAngle + 90);
  }

  public onClickNavigatorToggle(): void {
    if (this.showNavigator) {
      this.viewer.navigator.element.style.display = 'none';
    } else {
      this.viewer.navigator.element.style.display = 'inline-block';
    }
    this.showNavigator = !this.showNavigator;
  }

  public onClickFullscreenToggle(): void {
    this.fullscreenToggle.stateBehavior.onChange();
    this.viewer.setFullScreen(!this.isFullScreen);
  }

  /**
   * Handles the annotation button click.
   * Opens a loader modal if it will take more time,
   * fetches the kimera grid ID, and navigates to the annotation page.
   */
  public onClickAnnotateImage(): void {
    if (!this._selectedImage?.objectId) {
      this.dialogService.openError();
      return;
    }

    const spinnerModalRef = this.openAnnotationLoadingModal();
    const spinner = spinnerModalRef.componentInstance;
    const cancelSubscription = this.subscribeToCancelEvent(spinner);

    this.fetchKimeraGridIdAndNavigate(this._selectedImage.objectId, spinner, spinnerModalRef, cancelSubscription);
  }


  public onClickPreviousImage(): void {
    this.previousImage.emit();
  }

  public onClickNextImage(): void {
    this.nextImage.emit();
  }

  public onClickThumbnail(imageId: string): void {
    this.thumbnailClicked.emit(imageId);
  }

  public onCloseImageViewer(): void {
    if (this.isFullScreen) {
      // want to exit fullscreen not switch to grid
      this.viewer.setFullScreen(!this.isFullScreen);
    } else {
      this.closeImageViewer.emit();
    }
  }

  public onDownloadImage(): void {
    this.downloadImage.emit(this._selectedImage);
  }

  public onShareImage(): void {
    const shareFormModalInstanse = this.dialogService.open(ShareItemModalComponent, { windowClass: 'shareModalClass' });
    shareFormModalInstanse.componentInstance.clObjectId = this.selectedImage.objectId;
    shareFormModalInstanse.componentInstance.isDocumentShareable = true;
    shareFormModalInstanse.componentInstance.shareEntityType = 'ASSET';
  }

  public onAddToFavorites(): void {
    this.addToFavorites.emit(this._selectedImage);
  }

  public startOpenSeadragon(): void {
    initializeOpenSeaDragonSvgRelay(OpenSeaDragon);
    this.viewer = OpenSeaDragon(this.openSeadragonInitialSettings);
    this.addCanvasDragHandlers();
    this.hasRenderedOnce = true;
  }

  public displaySelectedImageInOpenSeadragon(): void {
    if (this.viewer) {
      if (!!this._selectedImage && !!this._selectedImage.url) {
        const tiledImage = {
          tileSource: {
            type: 'image',
            url: this._selectedImage.url,
          },
        };
        // Clear out the previous image to prevent overlapping
        this.viewer._cancelPendingImages();
        if (this.viewer.world.getItemCount()) {
          this.viewer.world.removeAll();
        }
        // Set the new image form render
        this.viewer.open(tiledImage);
      }
    }
  }

  public onClickImageRequest() {
    // Just a mock for the future logic
    const requestUnwatermarkedImageModelWindow = this.dialogService.open(RequestImageModalComponent, {
      size: 'lg',
      centered: true,
      windowClass: 'uui-modal',
    });
    requestUnwatermarkedImageModelWindow.componentInstance.imageTitle = this.toolbarTitle;
    requestUnwatermarkedImageModelWindow.componentInstance.analysisCode = this.selectedImage.analysisData.reportCode;
    requestUnwatermarkedImageModelWindow.componentInstance.clObjectID = this.selectedImage.objectId;
  }

  public getFullScreenToggle(): CustomOption {
    let fullScreenToggleOption: CustomOption;
    for (const toolbarGroup of this.toolbarOptions) {
      const options: Array<Option> = toolbarGroup.getOptions();
      fullScreenToggleOption = options.find((key: Option) => key.identifier === 'toggle-fullscreen') as CustomOption;
      if (fullScreenToggleOption) {
        break;
      }
    }
    return fullScreenToggleOption;
  }

  /**
   * Handles the toggle action for switching between HD and SD image resolutions.
   *
   * - Checks if calibration is required and whether the user has previously opted to skip the calibration warning.
   * - If calibration is required and the warning has not been skipped, opens a modal dialog to warn the user.
   *   - If the user chooses "Don't show again", stores this preference in sessionStorage.
   *   - If the dialog is dismissed, reverts the toggle state and aborts the resolution change.
   * - If the toggle is accepted, resets calibration and switches the image resolution:
   *   - If currently in HD, switches to SD.
   *   - If currently in SD, loads the HD image.
   */
  public onToggleImageResolutionClick(): void {
    const SKIP_KEY = 'iv.skipCalibrationWarning';
    const imageIsHD = this.toggleImageResolutionOption.stateBehavior.isPressed();

    // conditions to show the popup
    if (this.hasCalibration && !!!sessionStorage.getItem(SKIP_KEY)) {

      // show popup
      const dialogRef = this.dialogService.open(CalibrationModalWarningComponent, {
        windowClass: 'uui-modal modal-medium',
        size: 'sm',
        centered: true,
      });

      // if user clicks on "Don't show again", store in session storage
      (dialogRef.result as Promise<{ isDismissed: boolean; dontShowAgain: boolean }>)
        .then((res) => {
          if (res?.dontShowAgain) sessionStorage.setItem(SKIP_KEY, '1');
      })
      .catch((res) => {
          if (res?.dontShowAgain) sessionStorage.setItem(SKIP_KEY, '1');
      });

      // if user accepts the warning, proceed with the change
      dialogRef.closed.subscribe(() => {
        this.handleImageResolutionToggleChange(imageIsHD);
      });

      // if user dismisses the dialog, revert the toggle state
      dialogRef.dismissed.subscribe(() => {
        this.toggleImageResolutionOption.stateBehavior.onChange(); // revert the toggle state
      });

    } else {
      this.handleImageResolutionToggleChange(imageIsHD);
    }
  }

  protected getScalingFactor(): number {
    return this.calibration.scaleRef / this.calibration.scale;
  }

  protected prepareForMeasuring(): void {
    if (!this.hasCalibration) {
      this.dialogService.open(CalibrationInstructionsModalComponent, this.CALIBRATION_INSTRUCTIONS_MODAL_OPTIONS);
    }
  }

  protected async calibrateMeasurement(lineDistance: number): Promise<void> {
    try {
      const dialog = this.dialogService.open(ImageCalibrationModalComponent, {
        windowClass: 'calibration-dialog',
        size: 'sm',
        container: '.openseadragon-viewer',
      });
      const calibrationUnit: Calibration = { ...(await dialog.result), scale: lineDistance };
      this.annotationService
        .postSaveCalibration(this._selectedImage.id, calibrationUnit, this.calibration?.id)
        .subscribe();
      this.hasCalibration = true;
      this.calibration = calibrationUnit;
      return;
    } catch (error) {
      // user closed the modal
    }
  }

  protected displayMeasurement(measurement: Measurement): Promise<any> {
    const dialog = this.dialogService.open(ImageMeasurementDialogComponent, this.DEFAULT_MODAL_OPTIONS);
    dialog.componentInstance.calibration = this.calibration;
    dialog.componentInstance.measurement = measurement;
    dialog.componentInstance.clickRecalibrate.subscribe(() => this.onClickRecalibrate());
    return dialog.result;
  }

  private onClickRecalibrate() {
    this.hasCalibration = false;
    this.dialogService.open(CalibrationInstructionsModalComponent, this.CALIBRATION_INSTRUCTIONS_MODAL_OPTIONS);
  }

  /**
   * createToolbarOptions creates the OptionGroup array of buttons to pass to the generic toolbar.
   */
  private createToolbarOptions(): Array<OptionGroup> {const pageOption = [];
    const optionGroups = [];

    if(this.isToolEnabled('prevPage')) {
      pageOption.push(
        new CustomOption(
          'prev-page',
          { click: this.onClickPreviousImage.bind(this) },
          new StatelessBehavior(Icon.IMAGE_LEFT_ARROW, 'Previous')
        )
      );
    }

    if(this.isToolEnabled('nextPage')) {
      pageOption.push(
        new CustomOption(
          'next-page',
          { click: this.onClickNextImage.bind(this) },
          new StatelessBehavior(Icon.IMAGE_RIGHT_ARROW, 'Next')
        )
      );
    }

    const toggleOptions = [];
    if (!this.isTablet && this.enableToggleImageResolution && this.isToolEnabled('toggleImageResolution')) {
      const toggleText = 'Toggle between high-definition and standard view';
      this.toggleImageResolutionOption = new CustomLargeOption(
        'toggle-image-resolution',
        { click: this.onToggleImageResolutionClick.bind(this) },
        new ToggleBehavior(Icon.TOGGLE_OFF, toggleText, Icon.TOGGLE_ON, toggleText, false),
        'HD',
        false,
        true
      );
      toggleOptions.push(this.toggleImageResolutionOption);
      optionGroups.push(new OptionGroup(toggleOptions));
    }

    const zoomOptions = [];
    if (this.enableUnwatermarkedImageRequest && this.isToolEnabled('requestImage')) {
      zoomOptions.push(
        new CustomOption(
          'request-image',
          { click: this.onClickImageRequest.bind(this) },
          new StatelessBehavior(Icon.IMAGE_UNWATERMAKED_IMAGE_ACCESS, 'Request unwatermarked image')
        )
      );
    }

    if(this.isToolEnabled('zoomOut')) {
      zoomOptions.push(
        new CustomOption(
          'zoom-out',
          { click: this.onClickZoomOut.bind(this) },
          new StatelessBehavior(Icon.IMAGE_ZOOM_OUT, 'Zoom Out')
        )
      );
    }

    if(this.isToolEnabled('zoomIn')) {
      zoomOptions.push(
        new CustomOption(
          'zoom-in',
          { click: () => this.onClickZoomIn() },
          new StatelessBehavior(Icon.IMAGE_ZOOM_IN, 'Zoom In')
        )
      );
    }

    if(this.isToolEnabled('fitToPage')) {
      zoomOptions.push(
        new CustomOption(
          'fit-to-page',
          { click: this.onClickFitPage.bind(this) },
          new StatelessBehavior(Icon.IMAGE_FIT_PAGE, 'Fit to Page')
        )
      );
    }

    if(this.isToolEnabled('rotateRight')) {
      zoomOptions.push(
        new CustomOption(
          'rotate-right',
          { click: this.onClickRotateRight.bind(this) },
          new StatelessBehavior(Icon.IMAGE_RIGHT_ROTATE, 'Rotate Right')
        )
      );
    }

    if(this.isToolEnabled('toggleNavigator')) {
      zoomOptions.push( new CustomOption(
          'toggle-navigator',
          { click: this.onClickNavigatorToggle.bind(this) },
          new ToggleBehavior(Icon.IMAGE_NAVIGATOR, 'Show Navigator', Icon.IMAGE_NAVIGATOR, 'Hide Navigator', true)
        )
      );
    }

    if(this.isToolEnabled('toggleFullscreen')) {
      zoomOptions.push(
        new CustomOption(
          'toggle-fullscreen',
          { click: this.onClickFullscreenToggle.bind(this) },
          new ToggleBehavior(Icon.IMAGE_ENTER_FULLSCREEN, 'Full Screen', Icon.IMAGE_EXIT_FULLSCREEN, 'Exit Full Screen')
        )
      );
    }

    const shareOptions: SocialOption[] = [];
    if(this.isToolEnabled('shareImage')) {
      shareOptions.push(
        new SocialOption(
          'share-image',
          { click: this.onShareImage.bind(this) },
          new StatelessBehavior(Icon.IMAGE_SHARE, 'Share')
        )
      );
    }

    const otherOptions = [];
    if(this.isToolEnabled('closeViewer')) {
      otherOptions.push(
        new CustomOption(
          'close-viewer',
          { click: this.onCloseImageViewer.bind(this) },
          new StatelessBehavior(Icon.IMAGE_CLOSE, 'Close'),
          'Close'
        )
      );
    }

    optionGroups.push(new OptionGroup(pageOption));

    const measurementOptions: CustomOption[] = [];
    const lengthTooltipText = 'Measure length';

    if (this.isToolEnabled('toggleLengthSelection')) {
      this.lengthSelectionToggleOption = new CustomOption(
        'toggle-length-selection',
        { click: this.onToggleLengthSelection.bind(this) },
        new ToggleBehavior(Icon.MEASURE_LINE, lengthTooltipText, Icon.MEASURE_LINE, lengthTooltipText, false),
        null,
      );
      measurementOptions.push(this.lengthSelectionToggleOption);
      optionGroups.push(new OptionGroup(measurementOptions));
    }

    const annotationOptions: CustomOption[] = [];
    if(this.isToolEnabled('annotateImage') && this.enableImageAnnotation) {
      annotationOptions.push(
        new CustomOption(
          'annotate-image',
          { click: this.onClickAnnotateImage.bind(this) },
          new StatelessBehavior(Icon.IMAGE_ANNOTATE, 'Annotate Image')
        )
      );
      optionGroups.push(new OptionGroup(annotationOptions));
    }

    optionGroups.push(new OptionGroup(zoomOptions));

    if (this.downloadEnabled && this.isToolEnabled('downloadImage')) {
      const downloadOptions: SocialOption[] = [];
      const downloadTooltipMessage = 'Download';
      downloadOptions.push(
        new SocialOption(
          'download-image',
          { click: this.onDownloadImage.bind(this) },
          new StatelessBehavior(Icon.REPORT_VIEWER_DOWNLOAD, downloadTooltipMessage),
          null,
          !this.downloadEnabled
        )
      );
      optionGroups.push(new OptionGroup(downloadOptions));
    }

    if (this.addToFavoriteEnabled && this.isToolEnabled('addToFavorites')) {
      const socialOptions: SocialOption[] = [];
      socialOptions.push(
        new SocialOption(
          'add-to-favorites',
          { click: this.onAddToFavorites.bind(this) },
          new StatelessBehavior(Icon.IMAGE_FAVORITE, 'Add to Favorites')
        )
      );
      optionGroups.push(new OptionGroup(socialOptions));
    }

    optionGroups.push(new OptionGroup(shareOptions));
    optionGroups.push(new OptionGroup(otherOptions));

    return optionGroups;
}

  /**
   * createMetadataFromImage gathers metadata for the metadata panel.
   *  The order information is added here is the order it will appear in the UI.
   * @param image The image to gather metadata from.
   */
  private createMetadataFromImage(image: Image): Metadata {
    // The order information is added here is the order it will appear in the UI.
    const metadata: Metadata = new Metadata();
    // Summary data for analysis images
    if (image?.summaryData) {
      const {
        filename,
        analysisTitle,
        code,
        date,
        deviceName,
        devicePartNumber,
        manufacturer,
        deviceType,
        downstreamProduct,
        itemDescription,
      } = image.summaryData;
      metadata
        .addMetadata('File Name', filename)
        .addMetadata('Analysis Title', analysisTitle)
        .addMetadata('Product Code', code)
        .addMetadata('Date Completed', date)
        .addMetadata('Device Name', deviceName)
        .addMetadata('Device Part No', devicePartNumber)
        .addMetadata('Device Manufacturer', manufacturer)
        .addMetadata('Device Type', deviceType)
        .addMetadata('Downstream Product', downstreamProduct)
        .addMetadata('Description', itemDescription);
    }
    // Janet metadata (for genealogy in Search module):
    if (image?.janetdata) {
      const { name, manufacturer, deviceType, deviceCategory, marketAvailability, countryOfPurchase, itemDescription } =
        image.janetdata;
      metadata
        .addMetadata('Manufacturer', manufacturer)
        .addMetadata('Common Name', name)
        .addMetadata('Device Type', deviceType)
        .addMetadata('Device Category', deviceCategory)
        .addMetadata('Description', itemDescription)
        .addMetadata('Market Availability', marketAvailability)
        .addMetadata('Country of Purchase', countryOfPurchase);
    }
    // Image metadata:
    if (image?.metadata) {
      const { filename, perspective, features } = image.metadata;
      metadata
        .addMetadata('Filename', filename)
        .addMetadata('Perspective', perspective)
        .addMetadata('Feature', features);
    }
    return metadata;
  }

  /**
   * postTelemetryEventForOpeningImage posts telemetry event for opening image.
   *
   * NOTE: This method will need to be modified to send the appropriate payload
   *  for the type of image being displayed.  It should also not be called by
   *  displaySelectedImageInOpenSeaDragon so telemetry isn't sent for thumbnails.
   *  This method could be made generic and public, and called externally as needed.
   */
  private postTelemetryEventForOpeningImage() {
    // Check if the image is selected
    if (
      this._selectedImage &&
      (this._selectedImage.janetdata || (this._selectedImage.analysisData && this._selectedImage.summaryData))
     ) {
      let payload;

      if (this._selectedImage.janetdata) {
        const filetype = this._selectedImage.metadata.filename.split('.')[1];
        payload = {
          reportTitle: this.buildReportTitle(this._selectedImage.janetdata),
          reportId: this._selectedImage.reportId,
          reportCode: this._selectedImage.janetdata.genealogyCode,
          artifactName: this._selectedImage.metadata.filename,
          artifactType: filetype,
          subscriptionId: this.subscriptionId,
          channelId: this.channelId,
          downloadSizeBytes: 0,
        };
      } else {
        payload = {
          reportId: this._selectedImage.reportId,
          reportCode: this._selectedImage.analysisData.reportCode,
          reportTitle: this._selectedImage.summaryData.reportTitle
            ? this._selectedImage.summaryData.reportTitle
            : this._selectedImage.summaryData.analysisTitle,
          artifactName: this._selectedImage.summaryData.filename,
          artifactType: this._selectedImage.analysisData.fileType,
          subscriptionId: this.subscriptionId,
          channelId: this.channelId,
          downloadSizeBytes: 0,
        };
      }

      const applicationModule = moduleNameFromUrl(this.router.url);

      const event = new TelemetryEvent(TelemetryEventName.EVENT_VIEW_IN_BROWSER, payload, applicationModule);
      this.metaService.postTelemetryEvent(event).subscribe();
    }
  }

  private buildReportTitle(janetdata: any): string {
    const titleName = `${janetdata.partNumber} (${janetdata.name})`;
    return `${janetdata.manufacturer} ${janetdata.name ? titleName : janetdata.partNumber}`;
  }

  private isToolEnabled(option: string) {
    const config = this.toolbarConfig;
    if (typeof config === 'boolean') {
      return config;
    } else {
      return option in config && config[option];
    }
  }

  /**
   * Handles the toggling between standard definition (SD) and high definition (HD) image resolutions.
   *
   * When toggled, this method updates the selected image accordingly, displays it using OpenSeadragon,
   * and emits an event indicating the current resolution state.
   *
   * @param currentImageIsHD - A boolean indicating whether the currently displayed image is in HD.
   * If `true`, the method switches to the SD version; if `false`, it loads the HD version.
   */
  private handleImageResolutionToggleChange(currentImageIsHD: boolean): void {
    this.resetCalibration();

    if(currentImageIsHD) {
        // going back to SD
        this.selectedImage = this._selectedImageSDVersion;
        this.displaySelectedImageInOpenSeadragon();
        this.toggleImageResolution.emit(false);
      } else {
        // going to HD
        this.loadHighResolutionImage();
        this.toggleImageResolution.emit(true);
      }
    }

  /**
   * Loads the high-resolution version of the currently selected image.
   *
   * This method opens a spinner modal to indicate loading, then requests the high-resolution
   * image from the `thumbnailsService` using the selected image's `objectId`. Once the image is
   * retrieved, it updates the `selectedImage` with the new data and displays it using OpenSeadragon.
   * The spinner modal is closed after the operation completes, regardless of success or failure.
   * If an error occurs during the image retrieval, an error dialog is displayed.
   *
   * @private
   */
  private loadHighResolutionImage(): void {
    this._selectedImageSDVersion = this.selectedImage;

    const spinnerModalRef = this.dialogService.open(ImageLoadingModalComponent, {
      windowClass: 'spinner-dialog',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    const spinner = spinnerModalRef.componentInstance;
    spinner.start();

    this.thumbnailsService.getHighImageResolution(this.selectedImage.objectId)
      .pipe(
        finalize(() => {
          spinner.stop();
          spinnerModalRef.close();
        })
      )
      .subscribe({
        next: (resp) => {
          this.selectedImage = {
            id: resp.objectId,
            url: resp.signedUrl,
            objectId: resp.objectId,
            url_expires: resp.expiration,
            metadata: this.selectedImage.metadata,
          };
          this.displaySelectedImageInOpenSeadragon();
        },
        error: () => {
          this.dialogService.openError();
        },
      });
  }

  /**
   * Resets the calibration state of the image viewer component.
   *
   * This method clears any existing calibration data, disables calibration mode,
   * and resets the measuring tool to its initial state.
   *
   * @private
   */
  private resetCalibration(): void {
    this.hasCalibration = false;
    this.calibration = null;
    this.resetMeasuringTool();
  }

  /**
   * Opens the loading modal for annotation preparation.
   * @returns Modal reference with the spinner component
   * @private
   */
  private openAnnotationLoadingModal() {
    const spinnerModalRef = this.dialogService.open(ImageLoadingModalComponent, {
      windowClass: 'spinner-dialog',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    const spinner = spinnerModalRef.componentInstance;
    spinner.showMessageAfterMs = 4000; // Show helpful message after 4 seconds if still loading
    spinner.loadingMessage = 'Preparing image for annotation.<br />This may take a few moments.';
    spinner.start();

    return spinnerModalRef;
  }

  /**
   * Subscribes to the cancel event of the loading spinner.
   * @param spinner The spinner component instance
   * @returns Subscription to the cancel event
   * @private
   */
  private subscribeToCancelEvent(spinner: ImageLoadingModalComponent) {
    return spinner.cancel.subscribe(() => {
      // API subscription will be cleaned up by finalize
    });
  }

  /**
   * Fetches the kimera grid ID and navigates to the annotation page.
   * @param objectId The image object ID
   * @param spinner The spinner component instance
   * @param spinnerModalRef The modal reference
   * @param cancelSubscription The cancel subscription to clean up
   * @private
   */
  private fetchKimeraGridIdAndNavigate(
    objectId: string,
    spinner: ImageLoadingModalComponent,
    spinnerModalRef: any,
    cancelSubscription: any
  ): void {
    this.osdKimeraService.getKimeraGridIdFromObjectId(objectId, IMAGE_SOURCE_TYPE.CONTENT_LAKE)
      .pipe(
        finalize(() => this.closeAnnotationLoadingModal(spinner, spinnerModalRef, cancelSubscription)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (gridId) => this.navigateToAnnotationPage(gridId),
        error: () => this.dialogService.openError({
          title: 'Oops... Something went wrong',
          body: "We weren't able to prepare your image for annotation - please try again",
          closeButtonLabel: 'Close'
        }),
      });
  }


  private navigateToAnnotationPage(gridId: string): void {
    this.router.navigate(['/osd-annotation'], {
      queryParams: { gridId }
    });
  }

  private closeAnnotationLoadingModal(
    spinner: ImageLoadingModalComponent,
    spinnerModalRef: any,
    cancelSubscription: any
  ): void {
    spinner.stop();
    spinnerModalRef.close();
    cancelSubscription.unsubscribe();
  }

}
